import { useMemo } from 'react';
import { Focusable, IconButton, Link, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import clsx from 'clsx';

import styles from './typeAwareTag.module.css';

enum TagType {
  STRING = 'str',
  NUMBER = 'num',
  BOOLEAN = 'bool',
}

const Quotes = ({ children }: { children: React.ReactNode }) => (
  <>
    <span>“</span>
    <span className={styles.truncated}>{children}</span>
    <span>”</span>
  </>
);

/* This component detects whether a value is coercible between string, number or boolean.
 * A type toggle switch is displayed accordingly.
 *
 * Examples:
 * value={ "true" | true } - Toggle switches between bool / str
 * value={ 12 | "12" } 		 - Toggle switches between num / str
 * value={ "twelve" } 		 - No toggle switch displayed */
export const TypeAwareTag = ({
  value,
  onChange,
  onRemove,
}: {
  value: string | number | boolean;
  onChange: (castedValue: string | number | boolean) => void;
  onRemove: () => void;
}) => {
  const isString = typeof value === 'string';
  const isNumber = typeof value === 'number';
  const isBoolean = typeof value === 'boolean';
  const isNumberString = isString && !isNaN(Number(value));
  const isBooleanString = isString && (value === 'true' || value === 'false');
  const isTogglable = isNumber || isNumberString || isBoolean || isBooleanString;

  const typeText = useMemo(() => {
    if (isNumberString || isBooleanString) {
      return TagType.STRING;
    } else if (isNumber) {
      return TagType.NUMBER;
    } else if (isBoolean) {
      return TagType.BOOLEAN;
    }
  }, [isBooleanString, isNumberString, isNumber, isBoolean]);

  const labelText = useMemo(() => {
    if (typeText === TagType.NUMBER) {
      return 'Change type from number to string';
    } else if (typeText === TagType.BOOLEAN) {
      return 'Change type from boolean to string';
    } else if (typeText === TagType.STRING) {
      if (isBooleanString) {
        return 'Change type from string to boolean';
      } else if (isNumberString) {
        return 'Change type from string to number';
      }
    }
  }, [typeText, isBooleanString, isNumberString]);

  const valueText = useMemo(() => {
    const s = String(value);

    return isString ? <Quotes>{s}</Quotes> : <span className={styles.truncated}>{s}</span>;
  }, [isString, value]);

  return (
    <div className={clsx(styles.typeToggleTag)}>
      <TooltipTrigger>
        <Focusable>
          <span className={styles.value} role="button">
            {valueText}
          </span>
        </Focusable>
        <Tooltip placement="top">{valueText}</Tooltip>
      </TooltipTrigger>
      {isTogglable && (
        <TooltipTrigger>
          <Link
            className={clsx(styles.typeToggle, { [styles.isString]: isString })}
            onClick={() => {
              if (isNumberString) {
                onChange(Number(value));
              } else if (isBooleanString) {
                onChange(value === 'true' ? true : value === 'false' ? false : value);
              } else {
                onChange(String(value));
              }
            }}
            aria-label={labelText}
          >
            {typeText}
          </Link>
          <Tooltip placement="top">{labelText}</Tooltip>
        </TooltipTrigger>
      )}
      <IconButton
        className={styles.removeButton}
        aria-label="Remove tag"
        size="small"
        variant="minimal"
        icon="cancel-circle-outline"
        onClick={onRemove}
      />
    </div>
  );
};
